// Client for the server-side Gemini proxy.
//
// These calls used to run against the Gemini SDK directly in the browser,
// which required shipping the API key in the bundle and left the per-tier
// quota enforceable only in IndexedDB. Both now live behind /api/ai/*.

import { MediaItem, DriveAuthSession } from "../types";
import { searchCacheService } from "./searchCacheService";

const authHeaders = (session: DriveAuthSession): Record<string, string> => ({
  Authorization: `Bearer ${session.idToken || session.accessToken}`,
  'x-access-token': session.accessToken,
  'Content-Type': 'application/json',
});

const readError = async (response: Response, fallback: string): Promise<string> => {
  try {
    const data = await response.json();
    return data?.error || fallback;
  } catch {
    return fallback;
  }
};

export const analyzeImage = async (
  item: MediaItem,
  session: DriveAuthSession | null,
  prompt?: string
): Promise<string> => {
  if (!session) {
    return "Sign in is required to analyze images.";
  }

  try {
    let payload: Record<string, unknown>;
    const headers = authHeaders(session);

    if (item.source === 'local' && item.file) {
      payload = {
        source: 'local',
        dataBase64: await fileToBase64(item.file),
        mimeType: item.mimeType || item.file.type,
        prompt,
      };
    } else if (item.source === 'drive' && item.id) {
      // The server fetches the bytes from Drive itself using this token.
      headers['x-drive-access-token'] = session.accessToken;
      payload = { source: 'drive', fileId: item.id, mimeType: item.mimeType, prompt };
    } else {
      throw new Error("Invalid media item source or missing credentials");
    }

    const response = await fetch('/api/ai/analyze', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      return await readError(response, 'Failed to analyze image.');
    }

    const data = await response.json();
    return typeof data?.text === 'string' ? data.text : "No description available.";
  } catch (error) {
    console.error("Gemini Analysis Error:", error);
    return "Failed to analyze image. " + (error instanceof Error ? error.message : String(error));
  }
};

export const generateSearchTags = async (
  userQuery: string,
  session: DriveAuthSession | null,
  useCache: boolean = true
): Promise<string[]> => {
  if (!session) {
    // Fallback without a session: degrade to plain keyword search.
    return userQuery.split(' ');
  }

  // Layer 2: Check tag generation cache first
  if (useCache) {
    const cachedTags = await searchCacheService.getCachedTags(userQuery);
    if (cachedTags) {
      return cachedTags;
    }
  }

  try {
    const response = await fetch('/api/ai/searchTags', {
      method: 'POST',
      headers: authHeaders(session),
      body: JSON.stringify({ query: userQuery }),
    });

    if (!response.ok) {
      return [userQuery];
    }

    const data = await response.json();
    const tags = Array.isArray(data?.tags)
      ? data.tags.filter((tag: unknown): tag is string => typeof tag === 'string')
      : [];

    if (tags.length === 0) return [userQuery];

    if (useCache) {
      await searchCacheService.saveCachedTags(userQuery, tags);
    }

    return tags;
  } catch (error) {
    console.error("Gemini Search Expansion Error:", error);
    return [userQuery];
  }
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the "data:*/*;base64," prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};
